import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';

import { OverlayTrigger, Popover, ProgressBar } from 'react-bootstrap-5';
import * as api from 'helpers/api';
import { Alert } from '@buildoutinc/blueprint-react/ui/Alert';
import { Button } from '@buildoutinc/blueprint-react/ui/Button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle, faExclamationTriangle } from '@fortawesome/pro-solid-svg-icons';

export const TRANSFER_STATE = {
  PENDING: 'pending',
  IN_PROGRESS: 'in_progress',
  COMPLETED: 'completed',
  FAILED: 'failed'
};

export function TransferRecordsContainer({ transferRecordBatch, onReady }) {
  const [transferStatus, setTransferStatus] = useState({});
  const [showTransfer, setShowTransfer] = useState(false);
  const [startTransfer, setStartTransfer] = useState(false);

  useEffect(() => {
    if (!transferRecordBatch) return;

    setTransferStatus({});
    setShowTransfer(true);
    setStartTransfer(true);
  }, [transferRecordBatch]);

  useEffect(() =>{
    if (!startTransfer) return;

    const pollInterval = setInterval(pollTransferStatus, 1000);

    return () => {
      clearInterval(pollInterval);
    };
  }, [startTransfer]);

  const handleReload = () => {
    api.get(transferRecordBatch.resendPath).then(() => {
      setTransferStatus({});
      setStartTransfer(true);
    });
  };

  const handleDismiss = () => {
    api.get(transferRecordBatch.resetCachedTransfer).then(() => {
      setShowTransfer(false);
    });
  };

  const pollTransferStatus = () => {
    api.get(transferRecordBatch.statusPath).then((result) => {
      const { status, statusLabel, complete, total, errors } = result;
      const percentComplete = total > 0 ? Math.round(complete / total * 100) : 100;
      setTransferStatus({ status, statusLabel, percentComplete, errors });

      if (status === TRANSFER_STATE.COMPLETED || status === TRANSFER_STATE.FAILED) {
        setStartTransfer(false);

        if (status === TRANSFER_STATE.COMPLETED)  setTimeout(() => handleDismiss(), 6000);
        if (onReady)  onReady();
      }
    });
  };

  const renderInProgress = () => {
    const progressLabel = `${transferStatus.percentComplete}% of the ${transferRecordBatch.action} is complete`;
    const overlay = (
      <Popover id="transfer-description">{transferStatus.statusLabel}</Popover>
    );

    return (
      <OverlayTrigger overlay={overlay} placement="bottom">
        <ProgressBar className="m-5" label={progressLabel} now={transferStatus.percentComplete}/>
      </OverlayTrigger>
    );
  };

  const renderSuccess = () => {
    return (
      <Alert severity="success" withIcon>
        <FontAwesomeIcon icon={faCheckCircle} />
        <span>{transferStatus.statusLabel}</span>
        <div>
          <Button className="p-0" size="sm" variant="ghost" onClick={handleDismiss}>Dismiss</Button>
        </div>
      </Alert>
    );
  };

  const renderError = () => {
    const { statusLabel, errors } = transferStatus;
    const content = (
      <div className="align-self-center px-4">
        <p>
          {statusLabel} was <strong>unsuccessful.</strong>&nbsp;
          Please try again by <strong className="clickable" onClick={handleReload}>clicking here</strong>.
        </p>
        <ul>
          {errors.map((error, i) => <li key={i}>{error}</li>)}
        </ul>
      </div>
    );

    return (
      <Alert className="mb-0" severity="destructive" withIcon>
        <FontAwesomeIcon icon={faExclamationTriangle} />
        <span>{content}</span>
        <div>
          <Button size="sm" variant="secondary" onClick={handleDismiss}>Dismiss</Button>
        </div>
      </Alert>
    );
  };

  const renderBody = () => {
    switch (transferStatus.status) {
    case TRANSFER_STATE.PENDING:
      return renderInProgress();
    case TRANSFER_STATE.IN_PROGRESS:
      return renderInProgress();
    case TRANSFER_STATE.COMPLETED:
      return renderSuccess();
    case TRANSFER_STATE.FAILED:
      return renderError();
    default:
      return null;
    }
  };

  return showTransfer && renderBody();
}

TransferRecordsContainer.propTypes = {
  transferRecordBatch: PropTypes.object,
  onReady: PropTypes.func
};

export default TransferRecordsContainer;
